import type { BookingApiCallOptions } from '../api/bookingApi';
import { cancelBookingAsClient } from '../api/bookingApi';
import { AssetRequestError } from '../api/getAssetById';
import { normalizeBookingListRecords } from './bookingListNormalize';
import { bookingRecordPrimaryId } from './bookingRecordMutate';

const DISMISSED_BOOKING_IDS_STORAGE_KEY = 'ofrontend.bookings.dismissedOrphanIds';

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function normalizeBookingIdKey(id: string): string {
  return id.trim().toLowerCase();
}

export function bookingCancelErrorText(err: unknown): string {
  if (err instanceof AssetRequestError) {
    return [err.message, err.errorDescription, err.rawBody].filter(Boolean).join('\n');
  }
  if (err instanceof Error) {
    return err.message;
  }
  return String(err ?? '');
}

/**
 * Cancel failed because the linked asset or its owner no longer exists — booking can be hidden locally.
 */
export function isOrphanedBookingCancelError(err: unknown): boolean {
  const text = bookingCancelErrorText(err).toLowerCase();
  return (
    /owner not found/.test(text) ||
    /asset not found/.test(text) ||
    /delisted/.test(text) ||
    /no longer available/.test(text)
  );
}

export function loadDismissedBookingIds(): Set<string> {
  try {
    const raw = sessionStorage.getItem(DISMISSED_BOOKING_IDS_STORAGE_KEY);
    if (!raw) {
      return new Set();
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return new Set();
    }
    return new Set(
      parsed
        .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
        .map(normalizeBookingIdKey),
    );
  } catch {
    return new Set();
  }
}

export function rememberDismissedBookingId(bookingId: string): void {
  const key = normalizeBookingIdKey(bookingId);
  if (!key) {
    return;
  }
  const next = loadDismissedBookingIds();
  next.add(key);
  try {
    sessionStorage.setItem(DISMISSED_BOOKING_IDS_STORAGE_KEY, JSON.stringify(Array.from(next)));
  } catch {
    /* ignore quota */
  }
}

export function filterDismissedBookingsFromList(
  data: unknown,
  dismissedIds: ReadonlySet<string> = loadDismissedBookingIds(),
): unknown {
  if (dismissedIds.size === 0) {
    return data;
  }
  const keep = (row: Record<string, unknown>) => {
    const id = bookingRecordPrimaryId(row);
    return !id || !dismissedIds.has(normalizeBookingIdKey(id));
  };
  if (Array.isArray(data)) {
    return data.filter((row) => isPlainObject(row) && keep(row));
  }
  if (isPlainObject(data)) {
    const innerKey = ['bookings', 'data', 'items', 'results'].find((key) => Array.isArray(data[key]));
    if (innerKey) {
      const inner = data[innerKey] as unknown[];
      return {
        ...data,
        [innerKey]: inner.filter((row) => isPlainObject(row) && keep(row)),
      };
    }
  }
  return normalizeBookingListRecords(data).filter(keep);
}

export type ClientBookingCancelResult =
  | { mode: 'cancelled' }
  | { mode: 'dismissed_orphan' };

/** Client cancel; when asset/owner is gone, hide the booking locally instead of blocking the user. */
export async function cancelBookingAsClientResilient(
  bookingId: string,
  opts?: BookingApiCallOptions,
): Promise<ClientBookingCancelResult> {
  const trimmed = bookingId.trim();
  if (!trimmed) {
    throw new Error('Booking ID is required.');
  }
  try {
    await cancelBookingAsClient(trimmed, opts);
    return { mode: 'cancelled' };
  } catch (err) {
    if (!isOrphanedBookingCancelError(err)) {
      throw err;
    }
    rememberDismissedBookingId(trimmed);
    return { mode: 'dismissed_orphan' };
  }
}
